import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Avatar } from "../components/Avatar";
import { rankBadge, shortenReveal } from "../shared/format";
import { highlightName } from "../shared/highlight";
import { cleanProse } from "../shared/sanitize";
import { seasonSuperlatives, type Superlative } from "../shared/superlatives";
import {
  getLeagueState, getLeaderboardWithMovement, getRecentReveals, getSubjectStats, type Member,
} from "../lib/league";

// The season finale.
//
// Read-only and entirely additive: it renders existing data and touches no game
// logic, no scoring, no scheduling. It is gated on the season being complete so
// it cannot appear mid-season, with a commissioner-only preview so the host can
// see it before the league does.

type Beat = "champion" | "standings" | "superlatives" | "recap" | "close";

export function Finale() {
  const { leagueId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [failed, setFailed] = useState(false);
  const [beat, setBeat] = useState<Beat>("champion");
  // Lifted so the footer button can step back while the deck is still going.
  const [recapDone, setRecapDone] = useState(false);

  async function load() {
    setFailed(false);
    try {
      const { league, season, members, me } = await getLeagueState(leagueId!, user!.id);
      if (!season) { setData({ noSeason: true, league, me }); return; }
      const [board, updates, subjects] = await Promise.all([
        getLeaderboardWithMovement(season.id, members as Member[]),
        // The whole season, not the handful Home shows.
        getRecentReveals(season.id, 500),
        getSubjectStats(season.id),
      ]);
      const nameOf = (id: string) =>
        (members as Member[]).find((m) => m.user_id === id)?.display_name || "Friend";
      setData({
        league, season, members, me, board, updates, nameOf,
        superlatives: seasonSuperlatives(subjects, board, nameOf),
      });
    } catch {
      setFailed(true);
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [leagueId, user]);

  if (failed) {
    return (
      <div className="app">
        <div className="header">
          <h1>Season finale</h1>
          <button className="link" onClick={() => navigate(`/league/${leagueId}`)}>Home</button>
        </div>
        <div className="card">
          <p className="error">We couldn&apos;t load the finale.</p>
          <p className="muted">That is usually the connection. Try again.</p>
          <button className="btn primary" onClick={load}>Try again</button>
        </div>
      </div>
    );
  }
  if (!data) return <div className="app"><div className="loading">Putting the season together…</div></div>;

  const { league, season, me, board, updates, nameOf, superlatives } = data;
  const isComplete = season?.status === "complete";
  const isHost = me?.role === "commissioner";

  // Members must never see this before the season actually ends.
  if (!isComplete && !isHost) {
    return (
      <div className="app">
        <div className="header">
          <h1>Not yet</h1>
          <button className="link" onClick={() => navigate(`/league/${leagueId}`)}>Home</button>
        </div>
        <div className="card live">
          <p>The season is still running. This is where it all gets wrapped up when it&apos;s done.</p>
          <button className="btn primary" onClick={() => navigate(`/league/${leagueId}`)}>Back to league</button>
        </div>
      </div>
    );
  }

  const champion = board?.length && board[0].points > 0 ? board[0] : null;
  const withUpdates = (updates ?? []).filter((u: any) => (u.reveal_context ?? "").trim());
  const order: Beat[] = ["champion", "standings"];
  if (superlatives.length > 0) order.push("superlatives");
  if (withUpdates.length > 0) order.push("recap");
  order.push("close");
  const at = Math.max(0, order.indexOf(beat));
  const next = order[at + 1];

  return (
    <div className="app finale">
      <div className="header">
        <h1>{league?.name ?? "Season"}</h1>
        <button className="link" onClick={() => navigate(`/league/${leagueId}`)}>Home</button>
      </div>

      {!isComplete && (
        <div className="banner">
          <span>Preview. Only you can see this, and only until the season ends.</span>
        </div>
      )}

      <div className="beat-dots" aria-label={`Part ${at + 1} of ${order.length}`}>
        {order.map((b, i) => (
          <span key={b} className={`pip ${i < at ? "done" : i === at ? "current" : ""}`} />
        ))}
      </div>

      {beat === "champion" && <ChampionBeat league={league} champion={champion} youAre={user!.id} />}
      {beat === "standings" && <StandingsBeat board={board} youAre={user!.id} />}
      {beat === "superlatives" && <SuperlativesBeat items={superlatives} />}
      {beat === "recap" && <RecapBeat updates={withUpdates} nameOf={nameOf} onDone={() => setRecapDone(true)} />}
      {beat === "close" && <CloseBeat onHome={() => navigate(`/league/${leagueId}`)} />}

      {next && (
        // While the recap deck still has cards, the footer must not compete with
        // "Next one". A primary Finish sitting under an unfinished deck invites a
        // tap straight past the part the whole finale is built around.
        beat === "recap" && !recapDone ? (
          <button className="btn ghost" onClick={() => setBeat(next)}>Skip to the end</button>
        ) : (
          <button className="btn primary" onClick={() => setBeat(next)}>
            {next === "close" ? "Finish" : "Next"}
          </button>
        )
      )}
    </div>
  );
}

function ChampionBeat({ league, champion, youAre }: { league: any; champion: any; youAre: string }) {
  return (
    <div className="bloom">
      <p className="finale-kicker">That&apos;s a wrap on {league?.name ?? "the season"}</p>
      {champion ? (
        <div className="card champion">
          <span className="trophy">🏆</span>
          <p className="crown-label">Season champion</p>
          <Avatar id={champion.user_id} name={champion.name} size={92} />
          <h2 className="champion-name">{champion.name}</h2>
          <p className="muted">
            {champion.correct_count} right out of {champion.attempted_count}
            {champion.user_id === youAre ? " · that's you" : ""}
          </p>
        </div>
      ) : (
        <div className="card">
          <p>Nobody put up points this season, so there&apos;s no champion to crown. It happens.</p>
        </div>
      )}
    </div>
  );
}

function StandingsBeat({ board, youAre }: { board: any[]; youAre: string }) {
  return (
    <div className="card">
      <span className="tag">Final standings</span>
      {(board ?? []).map((e: any) => (
        <div className={`lb-row ${e.user_id === youAre ? "me" : ""}`} key={e.user_id}>
          <span className="lb-rank">{rankBadge(e.rank, e.points)}</span>
          <Avatar id={e.user_id} name={e.name} size={30} />
          <span className="lb-name">
            {e.name}{e.user_id === youAre ? " (you)" : ""}
            {e.attempted_count > 0 && (
              <>
                <br />
                <span className="muted lb-record">{e.correct_count}/{e.attempted_count} correct</span>
              </>
            )}
          </span>
          <span className="lb-points">{e.points}</span>
        </div>
      ))}
      <p className="muted">1 point per correct answer. No speed bonus.</p>
    </div>
  );
}

function SuperlativesBeat({ items }: { items: Superlative[] }) {
  return (
    <div className="card">
      <span className="tag">Season superlatives</span>
      {items.map((h) => (
        <div className="highlight" key={h.label}>
          <span className="highlight-label">{h.label}</span>
          <span className="highlight-name">{h.name}</span>
          <span className="muted highlight-detail">{h.detail}</span>
        </div>
      ))}
    </div>
  );
}

// The recap. A swipeable stack of everything the league shared this season,
// followed by the same list in full so nobody has to swipe to read it all.
function RecapBeat({
  updates, nameOf, onDone,
}: { updates: any[]; nameOf: (id: string) => string; onDone: () => void }) {
  const [idx, setIdx] = useState(0);
  const done = idx >= updates.length;
  useEffect(() => { if (done) onDone(); }, [done, onDone]);

  return (
    <>
      <div className="card">
        <span className="tag">What everybody got up to</span>
        <p className="muted">
          {done ? "That was the season." : "Swipe through, or use the button."}
        </p>
      </div>

      {!done && (
        <SwipeDeck
          key={idx}
          update={updates[idx]}
          nameOf={nameOf}
          remaining={updates.length - idx}
          onDone={() => setIdx((n) => n + 1)}
        />
      )}

      {done && (
        <div className="card">
          <span className="tag">All {updates.length} of them</span>
          <ul className="recently">
            {updates.map((u: any) => (
              <li key={u.drop_question_id}>
                <RecapLine update={u} nameOf={nameOf} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

function RecapLine({ update, nameOf }: { update: any; nameOf: (id: string) => string }) {
  const name = nameOf(update.subject_user_id);
  const parts = highlightName(shortenReveal(cleanProse(update.reveal_context ?? "")), name);
  return (
    <>
      {parts.map((p, i) =>
        p.isName ? <b key={i} className="who-name-hl">{p.text}</b> : <span key={i}>{p.text}</span>
      )}
    </>
  );
}

// Drag follows the thumb, then flings or snaps back. Pointer events so one code
// path covers touch and mouse; `touch-action: none` on the card is what stops
// the browser claiming the gesture for scrolling on a phone.
function SwipeDeck({
  update, nameOf, remaining, onDone,
}: { update: any; nameOf: (id: string) => string; remaining: number; onDone: () => void }) {
  const [dx, setDx] = useState(0);
  const [flungTo, setFlungTo] = useState<number | null>(null);
  const startX = useRef(0);
  const dragging = useRef(false);
  // The ref is the truth, the state is only for painting. Reading the release
  // decision off state made it depend on a re-render landing between the last
  // move and the release, so a fast flick could be judged as dx = 0 and snap
  // back instead of advancing.
  const dxRef = useRef(0);
  const THRESHOLD = 90;

  function down(e: React.PointerEvent) {
    if (flungTo !== null) return;
    dragging.current = true;
    startX.current = e.clientX;
    dxRef.current = 0;
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  }
  function move(e: React.PointerEvent) {
    if (!dragging.current) return;
    dxRef.current = e.clientX - startX.current;
    setDx(dxRef.current);
  }
  function up() {
    if (!dragging.current) return;
    dragging.current = false;
    const travelled = dxRef.current;
    dxRef.current = 0;
    if (Math.abs(travelled) > THRESHOLD) fling(travelled > 0 ? 1 : -1);
    else setDx(0);
  }
  function fling(dir: 1 | -1) {
    setFlungTo(dir);
    // Let the card clear the screen before the next one mounts.
    window.setTimeout(onDone, 220);
  }

  const offset = flungTo !== null ? flungTo * 700 : dx;
  const name = nameOf(update.subject_user_id);

  return (
    <div className="deck">
      <div
        className={`card recap-card ${flungTo !== null ? "flung" : ""} ${dragging.current ? "" : "settling"}`}
        style={{ transform: `translateX(${offset}px) rotate(${offset / 26}deg)`, opacity: flungTo !== null ? 0 : 1 }}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
      >
        <Avatar id={update.subject_user_id} name={name} size={64} />
        <h2 className="recap-name">{name}</h2>
        <p className="recap-text">{shortenReveal(cleanProse(update.reveal_context ?? ""), 160)}</p>
      </div>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
        <span className="muted">{remaining} left</span>
        <button className="btn ghost deck-next" onClick={() => fling(-1)}>Next one</button>
      </div>
    </div>
  );
}

function CloseBeat({ onHome }: { onHome: () => void }) {
  return (
    <div className="card live bloom">
      <span className="tag">That&apos;s the season</span>
      <p className="close-line">Thanks for showing up for each other.</p>
      <p className="muted">
        Everything above happened because people took a minute to say what was going on with them.
        That is the whole game.
      </p>
      <button className="btn primary" onClick={onHome}>Back to league</button>
    </div>
  );
}
